import React, { useEffect } from 'react';
import GenericTable, { LinkType } from '../../../shared/GenericTable';
import { useGetAllReservationsDetailedQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { Period } from '../../../enums/reports';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment-timezone';
import {
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { capitalizeName, formatCurrencyShort } from '../../../../shared/utils';
import { useSearchParams } from 'react-router-dom';
import UserRole from '../../../../shared/AuthRoles';
import { BookingType, Status } from '../../../enums/booking';

const CheckoutReservations = () => {
    const loggedInUser = UserRole();
    const [searchParams, setSearchParams] = useSearchParams();

    const period = searchParams.get('period');

    const { data, isLoading } = useGetAllReservationsDetailedQuery(
        {
            endDate: period as Period,
            status: [Status.Approved, Status.Completed],
            bookingType: loggedInUser?.role.includes('owner')
                ? [BookingType.Guest, BookingType.OnBehalf]
                : undefined,
        },
        { skip: !period },
    );

    useEffect(() => {
        if (!period) setSearchParams({ period: Period.CurrentWeek });
    }, [searchParams]);

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'module.name',
            header: 'Experience',
        },
        {
            accessorKey: 'startDate',
            header: 'Check-in',
            size: 120,
            filterVariant: 'date',
            filterFn: 'greaterThanOrEqualTo',
            sortingFn: 'datetime',
            mantineFilterDateInputProps: {
                valueFormat: 'll',
            },

            Cell: ({ cell }) => moment(cell.getValue<Date>()).format('ll'),
        },

        {
            accessorKey: 'endDate',
            header: 'Checkout',
            size: 120,
            filterVariant: 'date',
            filterFn: 'lessThanOrEqualTo',
            sortingFn: 'datetime',
            mantineFilterDateInputProps: {
                valueFormat: 'll',
            },
            Cell: ({ cell }) => moment(cell.getValue<Date>()).format('ll'),
        },
        {
            accessorKey: 'id',
            header: 'Ref',
            Cell: ({ cell }) => {
                return `#${cell.getValue()}`;
            },
        },
        ...(loggedInUser?.role.includes('owner')
            ? [
                  {
                      accessorKey: 'guest.name',
                      header: 'Guest Company',
                      size: 90,
                      filterFn: 'fuzzy',
                  },
              ]
            : []),
        {
            accessorKey: 'booker',
            header: 'Primary Guest',
            size: 90,
            filterFn: 'fuzzy',
            accessorFn: (row) =>
                row.booker
                    ? `${row.booker.firstName} ${row.booker.lastName}`
                    : '',
        },
        {
            accessorKey: 'bookingTotal.total',
            header: 'Amount',
            Cell: ({ cell }) => {
                return `${formatCurrencyShort(cell.getValue<number>())}`;
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            enableColumnFilter: false,
            size: 110,
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();

                return (
                    <CustomChip
                        sx={{ cursor: 'pointer' }}
                        label={capitalizeName(value)}
                        color={
                            ['completed', 'active'].includes(value)
                                ? 'green'
                                : value === 'cancelled'
                                  ? 'red'
                                  : ['approved', 'expired'].includes(value)
                                    ? 'yellow'
                                    : value === 'declined'
                                      ? 'gray'
                                      : 'blue'
                        }
                        variant='outlined'
                    />
                );
            },
        },
    ];

    const handleChange = (e: SelectChangeEvent<string | null>) => {
        if (!e.target.value) return;

        setSearchParams({ period: e.target.value });
    };

    return (
        <>
            <Typography variant='h1'>Checkout Reservations</Typography>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    py: 1,
                }}
            >
                <Select value={period} onChange={handleChange} size='small'>
                    <MenuItem value={Period.Today}>Today</MenuItem>
                    <MenuItem value={Period.Tomorrow}>Tomorrow</MenuItem>
                    <MenuItem value={Period.CurrentWeek}>This Week</MenuItem>
                    <MenuItem value={Period.PreviousWeek}>Last Week</MenuItem>
                    <MenuItem value={Period.NextWeek}>Next Week</MenuItem>
                    <MenuItem value={Period.CurrentMonth}>This Month</MenuItem>
                    <MenuItem value={Period.CurrentYear}>This Year</MenuItem>
                </Select>
            </Box>
            <GenericTable
                tableName='checkout-reservations'
                columns={columns}
                data={data ?? []}
                loading={isLoading}
                enableRowSelection={false}
                enableMultiRowSelection={false}
                initialState={{
                    sorting: [{ id: 'endDate', desc: true }],
                }}
                linkType={LinkType.Reservation}
            />
        </>
    );
};

export default CheckoutReservations;
